import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';

export default function EmailTest() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTestEmail = async () => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // Get environment variables
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
      const promptTemplateId = import.meta.env.VITE_EMAILJS_PROMPT_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
      const slackEmail = import.meta.env.VITE_EMAILJS_SLACK_EMAIL;

      // Check if environment variables are set
      if (!serviceId || !templateId || !publicKey || !promptTemplateId) {
        throw new Error('EmailJS environment variables are not properly configured');
      }

      // Send test email
      const result = await emailjs.send(
        serviceId,
        templateId,
        {
          to_email: slackEmail,
          from_name: 'EmailJS Test',
          from_email: 'test@example.com',
          subject: 'EmailJS Test',
          message: `This is a test email sent from the EmailJS test component at ${new Date().toLocaleString()}`
        },
        publicKey
      );

      console.log('Test email sent successfully:', result);
      
      // Also test the prompt download template
      const promptResult = await emailjs.send(
        serviceId,
        promptTemplateId,
        {
          to_email: slackEmail,
          from_name: 'EmailJS Test',
          from_email: 'test@example.com',
          subject: 'Prompt Download Test',
          message: 'Test Prompt: Sample Prompt'
        },
        publicKey
      );
      
      console.log('Test prompt download email sent successfully:', promptResult);
      setSuccess(true);
    } catch (err) {
      console.error('Error sending test email:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white shadow-md rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">EmailJS Test</h2>
      <p className="text-sm text-gray-600 mb-4">
        This will send a test email to the configured Slack email address using EmailJS.
      </p>
      
      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">Test email sent successfully!</p>
            </div>
          </div>
        </div>
      )}
      
      <button
        onClick={handleTestEmail}
        disabled={loading}
        className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader className="animate-spin h-5 w-5 mr-2" />
            Sending...
          </>
        ) : (
          'Send Test Email'
        )}
      </button>
      
      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
        <h3 className="text-sm font-medium text-gray-800 mb-2">EmailJS Configuration</h3>
        <p className="text-sm text-gray-700">
          To configure EmailJS, add the following to your <code>.env</code> file:
        </p>
        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
          VITE_EMAILJS_SERVICE_ID=your_service_id{'\n'}
          VITE_EMAILJS_TEMPLATE_ID=your_template_id{'\n'}
          VITE_EMAILJS_PROMPT_TEMPLATE_ID=your_prompt_template_id{'\n'}
          VITE_EMAILJS_PUBLIC_KEY=your_public_key{'\n'}
          VITE_EMAILJS_SLACK_EMAIL=leads-aaaahdgij3xgdwhoffnd4fd7uu@one80workspace.slack.com
        </pre>
      </div>
    </div>
  );
}